import { QueryKeys } from "@/constants/Query";
import axiosInstance from "@/lib/axios";
import { useInfiniteQuery } from "@tanstack/react-query";
import type * as fhirTypes from "fhir/r4";

interface EncountersResponse {
  encountersByDate: {
    date: string;
    encounters: EncountersResponse_Encounter[];
  }[];
  paging: {
    page: number;
    pageSize: number;
    hasNext: boolean;
    hasPrev: boolean;
    nextPage: number | null;
    prevPage: number | null;
  };
}

export interface EncountersResponse_Encounter {
  id: string;
  title: string;
  encounter: fhirTypes.Encounter;
  classFhir: string;
  hasLabs: boolean;
  hasRx: boolean;
  hasFiles: boolean;
  hasPostVisitMsgs: boolean;
  encounterStatus: EncountersResponse_EncounterStatus;
  priorCarePlanCount: number;
  hasAdminNotes: boolean;
  isTakeable: boolean;
  isConcurrent: boolean;
  serviceType?: EncountersResponse_ServiceType | null;
  // A Curai Classic (legacy DTC) visit brought onto a CHAI patient's chart by an account merge.
  isLegacy?: boolean;
}

export enum EncountersResponse_EncounterStatus {
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  INCOMPLETE = "INCOMPLETE",
  CANCELLED = "CANCELLED",
  IGNORED = "IGNORED",
  ARRIVED = "ARRIVED",
}

export enum EncountersResponse_ServiceType {
  GENERAL_PRACTICE = "GENERAL_PRACTICE",
  MEDICATION_REVIEWS = "MEDICATION_REVIEWS",
  PRESCRIPTIONS = "PRESCRIPTIONS",
}

interface EncountersParams {
  patientId?: string;
  notAfter?: string;
  eocs?: string;
}

export const useTimelineEncounters = ({
  patientId,
  notAfter,
  eocs,
}: EncountersParams) => {
  // Timezone offset from UTC in hours, we negate it because the API expects
  // the offset from UTC, not the offset to UTC and getTimezoneOffset returns
  // the offset to UTC.
  const tzOffset = -new Date().getTimezoneOffset() / 60;
  const params = {
    tzOffset,
    patientId,
    notAfter,
    eocs,
  };

  return useInfiniteQuery<EncountersResponse>({
    queryFn: async ({ pageParam }) => {
      const response = await axiosInstance.get<EncountersResponse>(
        "/charting/timeline/encounters",
        {
          params: {
            page: pageParam,
            ...params,
          },
        },
      );

      return response.data;
    },
    queryKey: QueryKeys.Patient.MedicalRecords.Timeline.ENCOUNTERS({
      patientId,
      tzOffset,
      notAfter,
      eocs,
    }),
    enabled: !!patientId,
    initialPageParam: 1,
    getNextPageParam: (lastPage: {
      paging: { hasNext: boolean; nextPage: number | null };
    }) => {
      if (lastPage.paging.hasNext) return lastPage.paging.nextPage;
    },
    select: (data) => data,
  });
};
